import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { COMPANY } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "plomberie", label: "Plomberie" },
  { value: "serrurerie", label: "Serrurerie" },
  { value: "electricite", label: "Électricité" },
  { value: "autre", label: "Autre / Je ne sais pas" },
];

const inputClass =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition-all duration-200";

export default function QuoteForm({ defaultService = "", source = "site" }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: defaultService, urgency: "urgent", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Merci de renseigner votre nom, téléphone et le service concerné.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, message: `[${source}] ${form.message}` });
      setDone(true);
      toast.success("Demande envoyée ! Nous vous rappelons dans les plus brefs délais.");
      setForm({ name: "", phone: "", email: "", service: defaultService, urgency: "urgent", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Appelez-nous au " + COMPANY.phone);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-[380px] flex flex-col items-center justify-center text-center"
        data-testid="contact-success"
      >
        <div className="h-16 w-16 rounded-2xl bg-brand-green/10 flex items-center justify-center mb-6">
          <Check className="h-8 w-8 text-brand-green" />
        </div>
        <h3 className="font-display font-bold text-ink text-2xl">Demande reçue</h3>
        <p className="text-ink-soft mt-3 max-w-xs">
          Merci ! Notre équipe vous recontacte dans les plus brefs délais.
        </p>
        <button onClick={() => setDone(false)} className="btn-outline mt-8" data-testid="contact-reset-btn">
          Nouvelle demande
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-semibold text-ink mb-2">Nom complet *</label>
          <input data-testid="contact-name" className={inputClass} placeholder="Jean Dupont"
            value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-ink mb-2">Téléphone *</label>
          <input data-testid="contact-phone" className={inputClass} placeholder="06 12 34 56 78"
            value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-ink mb-2">Email (optionnel)</label>
        <input data-testid="contact-email" className={inputClass} placeholder="jean@email.fr"
          value={form.email} onChange={(e) => set("email", e.target.value)} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-semibold text-ink mb-2">Service *</label>
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger data-testid="contact-service"
              className="w-full h-[46px] rounded-xl border-slate-200 text-ink focus:ring-2 focus:ring-brand/15 focus:border-brand">
              <SelectValue placeholder="Choisir un service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`service-opt-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-ink mb-2">Priorité</label>
          <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
            <SelectTrigger data-testid="contact-urgency"
              className="w-full h-[46px] rounded-xl border-slate-200 text-ink focus:ring-2 focus:ring-brand/15 focus:border-brand">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="urgent">Urgent (immédiat)</SelectItem>
              <SelectItem value="planifie">Planifié</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-ink mb-2">Décrivez votre besoin</label>
        <textarea data-testid="contact-message" rows={3} className={inputClass + " resize-none"}
          placeholder="Ex : fuite sous l'évier de la cuisine..."
          value={form.message} onChange={(e) => set("message", e.target.value)} />
      </div>
      <button type="submit" data-testid="contact-submit" disabled={loading}
        className="btn-primary w-full disabled:opacity-60">
        {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</>) : "Être rappelé rapidement"}
      </button>
    </form>
  );
}
